import type {
  BlockProps,
  CarouselProps,
  ComponentMeta,
  ContentGridProps,
  CustomDataTableProps,
  DynamicDataTableProps,
  EmbeddableProps,
  GridItemProps,
  GridProps,
  HtmlProps,
  IFrameProps,
  LinkProps,
  MultimediaProps,
  PageProps,
  ScribbleProps,
  SectionListProps,
  ContentListProps,
  TextProps,
  TimelineProps,
} from '@canvas-builder/types/canvas'
import { ComponentTypes, ContentGridLayoutTypes } from '@canvas-builder/types/canvas'
import type { CollectionPlayer } from '@collection-player/types/collection-player.type'

const pageMeta = {
  allowedChildren: [
    ComponentTypes.Block,
    ComponentTypes.Carousel,
    ComponentTypes.CollectionPlayer,
    ComponentTypes.ContentGrid,
    ComponentTypes.CustomDataTable,
    ComponentTypes.DataCharts,
    ComponentTypes.DynamicDataTable,
    ComponentTypes.Embeddable,
    ComponentTypes.Grid,
    ComponentTypes.Html,
    ComponentTypes.IFrame,
    ComponentTypes.Link,
    ComponentTypes.Multimedia,
    ComponentTypes.PageBreak,
    ComponentTypes.Scribble,
    ComponentTypes.Text,
    ComponentTypes.Timeline,
  ],
  defaultChildren: [],
  defaultData: {},
  defaultPrintable: true,
}

export const componentMeta = {
  [ComponentTypes.Root]: {
    ...pageMeta,
    allowedChildren: [
      ...pageMeta.allowedChildren,
      ComponentTypes.Page,
      ComponentTypes.SectionList,
      ComponentTypes.ContentList,
    ],
  },
  [ComponentTypes.Page]: pageMeta,
  [ComponentTypes.Grid]: {
    allowedChildren: [ComponentTypes.GridItem],
    defaultChildren: [
      {
        type: ComponentTypes.GridItem,
        children: [], // TODO: can be removed if BE allows empty grid items
      },
      {
        type: ComponentTypes.GridItem,
        children: [], // TODO: can be removed if BE allows empty grid items
      },
    ],
    defaultData: {
      columns: 2,
    } as GridProps,
    defaultPrintable: true,
  },
  [ComponentTypes.GridItem]: {
    allowedChildren: [
      ComponentTypes.Carousel,
      ComponentTypes.CollectionPlayer,
      ComponentTypes.ContentGrid,
      ComponentTypes.CustomDataTable,
      ComponentTypes.DataCharts,
      ComponentTypes.DynamicDataTable,
      ComponentTypes.Embeddable,
      ComponentTypes.Grid,
      ComponentTypes.Html,
      ComponentTypes.IFrame,
      ComponentTypes.Link,
      ComponentTypes.Multimedia,
      ComponentTypes.Text,
      ComponentTypes.Timeline,
    ],
    defaultChildren: [],
    defaultData: {
      span: 1,
    } as GridItemProps,
    defaultPrintable: true,
  },
  [ComponentTypes.Carousel]: {
    allowedChildren: [],
    defaultChildren: [],
    defaultData: {
      placeholder_height: '500px',
    } as CarouselProps,
    defaultPrintable: true,
  },
  [ComponentTypes.CustomDataTable]: {
    allowedChildren: [],
    defaultChildren: [],
    defaultData: {} as CustomDataTableProps,
    defaultPrintable: true,
  },
  [ComponentTypes.DynamicDataTable]: {
    allowedChildren: [],
    defaultChildren: [],
    defaultData: {} as DynamicDataTableProps,
    defaultPrintable: true,
  },
  [ComponentTypes.Text]: {
    allowedChildren: [],
    defaultChildren: [],
    defaultData: {
      content: '<p class="c-theme-preset--placeholder">Add some text here. 😎</p>',
    } as TextProps,
    defaultPrintable: true,
  },
  [ComponentTypes.Multimedia]: {
    allowedChildren: [],
    defaultChildren: [],
    defaultData: {
      type: 'image',
    } as MultimediaProps,
    defaultPrintable: true,
  },
  [ComponentTypes.Embeddable]: {
    allowedChildren: [],
    defaultChildren: [],
    defaultData: {
      type: 'app',
      src: null,
      width: '100%',
      height: '100px',
      file: null,
    } as EmbeddableProps,
    defaultPrintable: false,
  },
  [ComponentTypes.ContentGrid]: {
    allowedChildren: [],
    defaultChildren: [],
    defaultData: {
      items: [],
      size: 'medium',
      layout_type: ContentGridLayoutTypes.GRID,
    } as ContentGridProps,
    defaultPrintable: true,
  },
  [ComponentTypes.SectionList]: {
    allowedChildren: [],
    defaultChildren: [],
    defaultData: {
      sections: [],
    } as SectionListProps,
    singleton: false,
    defaultPrintable: true,
  },
  [ComponentTypes.ContentList]: {
    allowedChildren: [],
    defaultChildren: [],
    defaultData: {
      sections: [],
    } as ContentListProps,
    singleton: false,
    defaultPrintable: true,
  },
  [ComponentTypes.Html]: {
    allowedChildren: [],
    defaultChildren: [],
    defaultData: {
      html: '',
    } as HtmlProps,
    defaultPrintable: true,
  },
  [ComponentTypes.IFrame]: {
    allowedChildren: [],
    defaultChildren: [],
    defaultData: {
      src: null,
      width: '100%',
      height: '400px',
      add_session_context: false,
    } as IFrameProps,
    defaultPrintable: true,
  },
  [ComponentTypes.Link]: {
    allowedChildren: [],
    defaultChildren: [],
    defaultData: {
      alignment: 'center',
      canvas_anchor: '',
      color: '#ffffff',
      display_text: 'Sample',
      file: null,
      font_family: 'Roboto',
      font_size: 16,
      link_type: 'web',
      preview_type: 'solid-button',
      target: '_blank',
      url: '',
    } as LinkProps,
    defaultPrintable: false,
  },
  [ComponentTypes.PageBreak]: {
    allowedChildren: [],
    defaultChildren: [],
    defaultData: {},
    defaultPrintable: true,
  },
  [ComponentTypes.Timeline]: {
    allowedChildren: [],
    defaultChildren: [],
    defaultData: {
      events: [],
      layout: 'vertical',
    } as TimelineProps,
    defaultPrintable: true,
  },
  [ComponentTypes.CollectionPlayer]: {
    allowedChildren: [],
    defaultChildren: [],
    defaultData: {
      groups: [],
      name: '',
    } as CollectionPlayer,
    defaultPrintable: true,
  },
  [ComponentTypes.Block]: {
    allowedChildren: [],
    defaultChildren: [],
    defaultData: {
      id: '',
      overrides: {},
    } as BlockProps,
    singleton: false,
    defaultPrintable: true,
  },
  [ComponentTypes.Scribble]: {
    allowedChildren: [
      ComponentTypes.Multimedia,
      ComponentTypes.Text,
      ComponentTypes.DynamicDataTable,
      ComponentTypes.Html,
      ComponentTypes.Embeddable,
    ],
    defaultChildren: [],
    defaultData: {} as ScribbleProps,
    defaultPrintable: true,
  },
  [ComponentTypes.DataCharts]: {
    allowedChildren: [],
    defaultChildren: [],
    defaultData: {
      chartType: 'bar',
      data: [],
      options: {
        title: 'New Chart',
        xAxis: '',
        yAxis: '',
      },
    },
    defaultPrintable: true,
  },
} as Record<ComponentTypes, ComponentMeta>

export const defaultWhenUsedInSection = {
  is_editable: false,
  is_removable: false,
}

export const SERVER_ONLY_PROPS = [
  'content_url',
  'thumbnail_url',
  'content_thumbnails',
  'pspdfkit_token',
  'pspdfkit_server_url',
  'pspdfkit_auth_payload',
  'external_url',
] as const

export const aspectRatios = ['unlocked', 'contain', '16:9', '16:10', '4:3', '1:1'] as const
// @unocss-include
export const aspectRatioClasses = {
  '16:9': 'aspect-ratio-video',
  '16:10': 'aspect-ratio-[16/10]',
  '1:1': 'aspect-ratio-square',
  '4:3': 'aspect-ratio-[4/3]',
  unlocked: '',
  contain: '',
} as const satisfies Record<NonNullable<PageProps['aspect_ratio']>, string>
